import React, { useState, useEffect, useRef } from 'react';
import {
  QrCode,
  ScanLine,
  Camera,
  X,
  Keyboard,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Sparkles,
  Search,
} from 'lucide-react';
import { Siswa } from '../../types/index.js';

interface BarcodeScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  siswaList: Siswa[];
  onSelectSiswa: (siswa: Siswa) => void;
}

export const BarcodeScannerModal: React.FC<BarcodeScannerModalProps> = ({
  isOpen,
  onClose,
  siswaList,
  onSelectSiswa,
}) => {
  const [scanMode, setScanMode] = useState<'HARDWARE_USB' | 'WEBCAM' | 'MANUAL'>('HARDWARE_USB');
  const [scannedCode, setScannedCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMatch, setSuccessMatch] = useState<Siswa | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Auto focus input whenever modal opens or mode changes
  useEffect(() => {
    if (isOpen) {
      setErrorMessage(null);
      setSuccessMatch(null);
      setScannedCode('');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      stopCamera();
    }
  }, [isOpen, scanMode]);

  // Clean up camera stream on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const stopCamera = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    setCameraActive(false);
  };

  const startCamera = async () => {
    try {
      setErrorMessage(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      mediaStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setCameraActive(true);
    } catch (err: any) {
      setErrorMessage(
        'Kamera tidak dapat diakses atau izin ditolak. Silakan gunakan mode Scanner USB / Input Kode Cepat di bawah.'
      );
      setCameraActive(false);
    }
  };

  // Process code (NIS, No. VA, or ID)
  const processCode = (rawCode: string) => {
    const cleanCode = rawCode.trim();
    if (!cleanCode) return;

    setErrorMessage(null);

    // Look for exact match by:
    // 1. NIS (e.g. 2448, 1213, 2450)
    // 2. Full No. VA (e.g. 8998650000002448 or suffix)
    // 3. QR Data JSON / URI string (e.g. SDMT:2448 or VA:899865...)
    let matched = siswaList.find((s) => s.nis === cleanCode);

    if (!matched) {
      matched = siswaList.find((s) => s.noVA === cleanCode);
    }

    if (!matched) {
      matched = siswaList.find(
        (s) => cleanCode.endsWith(s.nis) || (s.noVA && cleanCode.includes(s.noVA))
      );
    }

    if (!matched) {
      matched = siswaList.find(
        (s) => s.nama.toLowerCase() === cleanCode.toLowerCase()
      );
    }

    if (matched) {
      setSuccessMatch(matched);
      // Automatically choose after brief visual confirmation
      setTimeout(() => {
        onSelectSiswa(matched!);
        onClose();
      }, 400);
    } else {
      setErrorMessage(
        `Kode "${cleanCode}" tidak terdaftar di database siswa. Pastikan barcode kartu NIS atau kartu VA siswa SDMT valid.`
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      processCode(scannedCode);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      id="barcode-scanner-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto animate-in fade-in"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full border border-slate-200 overflow-hidden relative"
      >
        {/* Top Header */}
        <div className="bg-sky-800 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-sky-700/80 rounded-lg">
              <ScanLine className="w-5 h-5 text-amber-300 animate-pulse" />
            </div>
            <div>
              <h3 className="font-bold text-sm sm:text-base">Scanner Cepat Barcode / QR Siswa</h3>
              <p className="text-xs text-sky-200">
                Pindai Kartu NIS atau Kartu Virtual Account Bank Muamalat Siswa
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-sky-700 text-sky-200 hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode Selector Tabs */}
        <div className="bg-slate-100 p-2 flex gap-1 border-b border-slate-200 text-xs">
          <button
            type="button"
            onClick={() => {
              stopCamera();
              setScanMode('HARDWARE_USB');
            }}
            className={`flex-1 py-2 px-3 rounded-lg font-semibold flex items-center justify-center gap-1.5 transition cursor-pointer ${
              scanMode === 'HARDWARE_USB'
                ? 'bg-white text-sky-900 shadow-xs border border-slate-200'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <ScanLine className="w-3.5 h-3.5" />
            <span>Barcode Scanner USB / Bluetooth</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setScanMode('WEBCAM');
              startCamera();
            }}
            className={`flex-1 py-2 px-3 rounded-lg font-semibold flex items-center justify-center gap-1.5 transition cursor-pointer ${
              scanMode === 'WEBCAM'
                ? 'bg-white text-sky-900 shadow-xs border border-slate-200'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Camera className="w-3.5 h-3.5" />
            <span>Kamera / Webcam</span>
          </button>

          <button
            type="button"
            onClick={() => {
              stopCamera();
              setScanMode('MANUAL');
            }}
            className={`flex-1 py-2 px-3 rounded-lg font-semibold flex items-center justify-center gap-1.5 transition cursor-pointer ${
              scanMode === 'MANUAL'
                ? 'bg-white text-sky-900 shadow-xs border border-slate-200'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Keyboard className="w-3.5 h-3.5" />
            <span>Input Cepat</span>
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-5 space-y-4">
          {/* Hardware USB Mode */}
          {scanMode === 'HARDWARE_USB' && (
            <div className="space-y-3">
              <div className="bg-sky-50/70 border border-sky-200 rounded-xl p-3.5 text-xs text-sky-900 flex items-start gap-2.5">
                <Sparkles className="w-4 h-4 text-sky-700 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-bold">Mode Siap Pindai (Hardware Barcode Reader Active)</p>
                  <p className="text-sky-800 text-[11px] leading-relaxed">
                    Arahkan scanner barcode/QR genggam (USB laser / Bluetooth gun) ke kartu siswa. Scanner akan otomatis mengisi kode dan memilih data siswa seketika.
                  </p>
                </div>
              </div>

              {/* Visual Scanner Animation Card */}
              <div className="relative border-2 border-dashed border-sky-300 rounded-xl bg-slate-900 text-white p-6 flex flex-col items-center justify-center overflow-hidden min-h-[160px]">
                <div className="absolute inset-0 bg-radial from-sky-500/10 to-transparent pointer-events-none"></div>

                {/* Laser scan line animation */}
                <div className="absolute top-0 left-4 right-4 h-0.5 bg-gradient-to-r from-transparent via-rose-500 to-transparent shadow-[0_0_12px_#f43f5e] animate-bounce duration-1000"></div>

                <QrCode className="w-16 h-16 text-sky-400 opacity-60 mb-2" />
                <p className="font-mono text-xs text-sky-200 text-center font-bold tracking-wider">
                  MENUNGGU INPUT BARCODE SCANNER...
                </p>
                <p className="text-[10px] text-slate-400 mt-1">
                  Atau ketik NIS / Nomor VA langsung pada kolom di bawah
                </p>
              </div>
            </div>
          )}

          {/* Webcam Mode */}
          {scanMode === 'WEBCAM' && (
            <div className="space-y-3">
              <div className="relative bg-slate-950 rounded-xl overflow-hidden min-h-[220px] flex items-center justify-center border border-slate-700">
                <video
                  ref={videoRef}
                  className="w-full h-56 object-cover"
                  autoPlay
                  playsInline
                  muted
                />

                {cameraActive && (
                  <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center">
                    {/* Viewfinder box */}
                    <div className="w-44 h-44 border-2 border-amber-400 rounded-lg relative shadow-[0_0_15px_rgba(251,191,36,0.3)]">
                      <div className="absolute top-0 left-0 right-0 h-0.5 bg-rose-500 shadow-[0_0_8px_#f43f5e] animate-bounce"></div>
                    </div>
                    <span className="text-[10px] bg-black/70 text-amber-300 px-2 py-0.5 rounded mt-2 font-mono">
                      Posisikan Barcode di dalam bingkai
                    </span>
                  </div>
                )}

                {!cameraActive && (
                  <div className="text-center p-4 space-y-2">
                    <Camera className="w-8 h-8 text-slate-500 mx-auto" />
                    <p className="text-xs text-slate-400">Kamera dinonaktifkan atau belum diizinkan</p>
                    <button
                      type="button"
                      onClick={startCamera}
                      className="px-3 py-1.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold rounded-lg transition"
                    >
                      Buka Kamera
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Unified Input Field for Scanner Emulation & Quick Typing */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-slate-700">
              Input Kode Kartu Siswa (NIS atau No. VA):
            </label>
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={scannedCode}
                onChange={(e) => setScannedCode(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Scan kartu atau ketik misal: 2448, 1213, 899865..."
                className="w-full pl-9 pr-24 py-2.5 text-sm font-mono font-bold border-2 border-sky-400 rounded-xl focus:ring-2 focus:ring-sky-500 focus:outline-hidden text-slate-900 bg-white"
                autoFocus
              />
              <ScanLine className="w-4 h-4 text-sky-600 absolute left-3 top-3.5" />
              <button
                type="button"
                onClick={() => processCode(scannedCode)}
                className="absolute right-1.5 top-1.5 bottom-1.5 px-3 bg-sky-700 hover:bg-sky-600 text-white rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer"
              >
                <span>Cari</span>
              </button>
            </div>
          </div>

          {/* Feedback states */}
          {errorMessage && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 flex items-start gap-2 animate-in fade-in">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {successMatch && (
            <div className="p-3.5 bg-emerald-50 border border-emerald-300 rounded-xl text-xs text-emerald-900 flex items-center justify-between animate-in zoom-in-95">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="font-extrabold text-sm">{successMatch.nama}</p>
                  <p className="text-emerald-700 text-[11px] font-mono">
                    NIS: {successMatch.nis} | Kelas: {successMatch.kelasNama}
                  </p>
                </div>
              </div>
              <span className="text-[10px] bg-emerald-200 text-emerald-900 font-extrabold px-2 py-0.5 rounded">
                Ditemukan
              </span>
            </div>
          )}

          {/* Sample quick tags for demo testing */}
          <div className="pt-2 border-t border-slate-100">
            <span className="text-[11px] text-slate-500 font-semibold block mb-2">
              Uji Coba Cepat Barcode Kartu Siswa (Klik Salah Satu):
            </span>
            <div className="grid grid-cols-3 gap-2">
              {siswaList.slice(0, 3).map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setScannedCode(s.nis);
                    processCode(s.nis);
                  }}
                  className="p-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-sky-50 hover:border-sky-300 text-left transition cursor-pointer"
                >
                  <p className="font-mono font-bold text-sky-800 text-xs">{s.nis}</p>
                  <p className="text-[10px] text-slate-600 truncate">{s.nama}</p>
                  <p className="text-[9px] text-slate-400">{s.kelasNama}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex items-center justify-between text-xs text-slate-500">
          <span>Kompatibel dengan semua jenis barcode scanner USB & Virtual Account Bank Muamalat</span>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-semibold transition cursor-pointer"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};
